import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReportType, GetReportResponse } from '@daynote/shared-types';
import { getDataSource } from '../data';
import { SectionCard } from '../components/ui/SectionCard';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { isElectron } from '../lib/electron';

function fmt(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shift(cursor: string, type: ReportType, dir: 1 | -1): string {
  const [y, m, d] = cursor.split('-').map(Number);
  if (type === 'month') return fmt(new Date(y!, m! - 1 + dir, 1));
  return fmt(new Date(y!, m! - 1, d! + dir * 7));
}

function toMarkdown(r: GetReportResponse, tk: (key: string) => string): string {
  const titleKey = r.type === 'week' ? 'reports.markdown.weekly' : 'reports.markdown.monthly';
  const title = tk(titleKey);
  const totalLabel = tk('reports.markdown.total');
  const itemsLabel = tk('reports.markdown.items');
  const doneLabel = tk('reports.markdown.done');
  const ungroupedLabel = tk('reports.markdown.ungrouped');
  const untrackedLabel = tk('reports.markdown.untracked');
  const lines: string[] = [
    `# ${title}（${r.start} ~ ${r.end}）`,
    '',
    `${totalLabel}：${r.totalHours}h · ${r.totalItems} ${itemsLabel} · ${doneLabel} ${r.doneItems} ${itemsLabel}`,
    '',
  ];
  for (const s of r.sections) {
    const label = s.group ? `${s.group.name} (${s.group.abbr})` : ungroupedLabel;
    lines.push(`## ${label} — ${s.totalHours}h / ${s.itemCount} ${itemsLabel}`);
    for (const it of s.items) {
      const hours = it.hours != null ? ` (${it.hours}h)` : '';
      const done = it.isDone ? ' ✓' : '';
      lines.push(`- ${it.content}${hours}${done}`);
    }
    if (s.untrackedCount > 0) lines.push(`- （${s.untrackedCount} ${untrackedLabel}）`);
    lines.push('');
  }
  return lines.join('\n').trim();
}

export function ReportsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [type, setType] = useState<ReportType>('week');
  const [cursor, setCursor] = useState(fmt(new Date()));
  const [report, setReport] = useState<GetReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await getDataSource().getReport(type, cursor));
    } finally {
      setLoading(false);
    }
  }, [type, cursor]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleCopy = async () => {
    if (!report) return;
    await navigator.clipboard.writeText(toMarkdown(report, t));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen p-6 md:p-8 max-w-3xl mx-auto">
      <header className="flex justify-between items-center mb-8">
        <button
          type="button"
          onClick={() => navigate('/calendar')}
          className="text-sm text-daynote-muted hover:text-daynote-text bg-transparent border-none cursor-pointer"
        >
          {t('reports.backToCalendar')}
        </button>
        <h1 className="font-display text-3xl">{t('reports.title')}</h1>
        {isElectron ? <div className="w-16" /> : <LanguageSwitcher />}
      </header>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex gap-1 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg p-1">
          {(['week', 'month'] as ReportType[]).map((rtype) => (
            <button
              key={rtype}
              type="button"
              onClick={() => setType(rtype)}
              className={`px-4 py-1.5 text-sm rounded-md cursor-pointer border-none ${
                type === rtype
                  ? 'bg-daynote-accent text-[#2a2520] font-medium'
                  : 'bg-transparent text-daynote-muted'
              }`}
            >
              {rtype === 'week' ? t('reports.weekly') : t('reports.monthly')}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCursor((c) => shift(c, type, -1))}
            className="w-8 h-8 rounded-full border border-[var(--grid-line)] text-daynote-muted hover:text-daynote-text bg-transparent cursor-pointer"
          >
            ←
          </button>
          <span className="text-sm text-daynote-text/70 min-w-44 text-center">
            {report ? `${report.start} ~ ${report.end}` : '…'}
          </span>
          <button
            type="button"
            onClick={() => setCursor((c) => shift(c, type, 1))}
            className="w-8 h-8 rounded-full border border-[var(--grid-line)] text-daynote-muted hover:text-daynote-text bg-transparent cursor-pointer"
          >
            →
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-daynote-muted py-10 text-center">{t('reports.loading')}</div>}

      {!loading && report && (
        <>
          <div className="flex gap-4 px-5 py-4 bg-white/[0.03] rounded-lg border border-[var(--grid-line)] mb-6">
            <div>
              <div className="font-display text-[24px] font-medium">{report.totalHours}h</div>
              <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('reports.totalHours')}</div>
            </div>
            <div className="w-px bg-[var(--grid-line)]" />
            <div>
              <div className="font-display text-[24px] font-medium">{report.totalItems}</div>
              <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('reports.totalItems')}</div>
            </div>
            <div className="w-px bg-[var(--grid-line)]" />
            <div>
              <div className="font-display text-[24px] font-medium">{report.doneItems}</div>
              <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('reports.doneItems')}</div>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleCopy}
              disabled={report.totalItems === 0}
              className="self-center px-4 py-2 text-sm bg-daynote-accent text-[#2a2520] rounded-md font-medium border-none cursor-pointer disabled:opacity-50"
            >
              {copied ? t('reports.copied') : t('reports.copyMarkdown')}
            </button>
          </div>

          {report.totalItems === 0 && (
            <div className="text-sm text-daynote-muted py-10 text-center">{t('reports.noItems')}</div>
          )}

          <div className="space-y-4">
            {report.sections.map((s) => {
              const label = s.group ? `${s.group.name}` : t('reports.ungrouped');
              return (
                <SectionCard
                  key={s.group?.id ?? '__none__'}
                  title={
                    s.group
                      ? `[${s.group.abbr}] ${label} · ${s.totalHours}h`
                      : `${t('reports.ungrouped')} · ${s.totalHours}h`
                  }
                >
                  <div className="text-xs text-daynote-text/50 mb-3">
                    {t('reports.itemCount', { count: s.itemCount, done: s.doneCount })}
                    {s.untrackedCount > 0 && t('reports.untrackedSuffix', { count: s.untrackedCount })}
                  </div>
                  <ul className="space-y-1.5">
                    {s.items.map((it) => (
                      <li
                        key={it.id}
                        className={`flex items-baseline gap-2 text-sm ${it.isDone ? 'line-through text-white/45' : ''}`}
                      >
                        <span className="text-[11px] text-daynote-muted font-display flex-shrink-0">
                          {it.date.slice(5)}
                        </span>
                        <span className="break-all flex-1">{it.content}</span>
                        {it.hours != null && (
                          <span className="text-xs text-daynote-muted flex-shrink-0">{it.hours}h</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
